import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import Layout from '../../layouts/Layout';
import { apiFetch, downloadPdf } from '../../api';
import '../master/Products.css';
import DateTimeText from '../../components/DateTimeText';
import Pagination from "../../components/Pagination";

export default function PackagingIndex() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Filter states
  const [showInvoiceFilter, setShowInvoiceFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const totalItems = useMemo(() => {
    return filteredRows.length;
  }, [filteredRows]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / rowsPerPage);
  }, [totalItems, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePaginatedUsers = useMemo(() => {
    return filteredRows.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  }, [filteredRows, currentPage, rowsPerPage]);

  /* =========================
     FETCH PACKAGING SLIPS
     ========================= */
  useEffect(() => {
    fetchPackagingSlips();
  }, []);

  const fetchPackagingSlips = async () => {
    try {
      const res = await apiFetch('/api/packaging-slip/get-packaging-slip');
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setRows(list);
      setFilteredRows(list);
    } catch (err) {
      console.error('Failed to load packaging slips', err);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  /* FILTER LOGIC
     ========================= */
  useEffect(() => {
    let filtered = rows;

    if (invoiceFilter) {
      filtered = filtered.filter(row =>
        row.invoice_no?.toLowerCase().includes(invoiceFilter.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(row =>
        row.customer_name?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    setFilteredRows(filtered);
  }, [invoiceFilter, customerFilter, rows]);

  const handleInvoiceFilterChange = (e) => {
    setInvoiceFilter(e.target.value);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
  };

  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async (id, invoiceNo) => {
    try {
      setDownloadingPdf(id);
      
      const blob = await downloadPdf(`/pdf-packaging.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Packing_List_${invoiceNo}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  /* =========================
     UI
     ========================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading packaging slips...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* ================= HEADER ================= */}
      <div className="products-toolbar">
        <div>
          <h1 className="products-title">Packaging Slips</h1>
          <p className="products-subtitle">
            Dispatch / packaging slip records
          </p>
        </div>

        <div>
          <button
            className="btn-primary"
            onClick={() => navigate('/packagin-slip')}
          >
            + Create Packaging Slip
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
     
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Invoice No
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: invoiceFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowInvoiceFilter(!showInvoiceFilter)}
                      title="Filter by Invoice No"
                    />
                  </div>
                  {showInvoiceFilter && (
                    <input
                      type="text"
                      placeholder="Filter invoice..."
                      value={invoiceFilter}
                      onChange={handleInvoiceFilterChange}
                      onBlur={() => setTimeout(() => setShowInvoiceFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Invoice Date</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Customer
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: customerFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowCustomerFilter(!showCustomerFilter)}
                      title="Filter by Customer"
                    />
                  </div>
                  {showCustomerFilter && (
                    <input
                      type="text"
                      placeholder="Filter customer..."
                      value={customerFilter}
                      onChange={handleCustomerFilterChange}
                      onBlur={() => setTimeout(() => setShowCustomerFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Order No</th>
                <th>Order Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

           <tbody>
      {handlePaginatedUsers.length === 0 ? (
        <tr>
          <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
            No packaging slips found
          </td>
        </tr>
      ) : (
       handlePaginatedUsers?.map((row) => ( 
      <tr key={row.id}>
        <td>{row.id}</td>
        <td>{row.invoice_no}</td>
        <td><DateTimeText value={row.invoice_date} /></td>
        <td>{row.customer_name}</td>
        <td>{row.order_no? row.order_no:'-'}</td>
        <td>{row.order_date ? new Date(row.order_date).toLocaleDateString('en-GB') : '-'}</td>
        <td>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              background: row.status === 'submit' ? '#d1fae5' : '#fee2e2',
              color: row.status === 'submit' ? '#065f46' : '#991b1b',
            }}
          >
            {row.status === 'approved' ? 'approved' : 'Cancelled'}
          </span>
        </td>
        <td>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="btn-link"
              onClick={() => navigate(`/packagin-slip/${row.id}`)}
            >
              View
            </button>

            <button
              className="btn-link"
              onClick={() => handleDownloadPdf(row.id, row.invoice_no)}
              disabled={downloadingPdf === row.id}
              style={{
                opacity: downloadingPdf === row.id ? 0.6 : 1,
                cursor: downloadingPdf === row.id ? 'not-allowed' : 'pointer'
              }}
            >
              {downloadingPdf === row.id ? 'Downloading...' : 'PDF'}
            </button>
          </div>
        </td>
      </tr>
    ))
  )}
</tbody>
          </table>
        </div>
       <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
    </Layout>
  );
}
